import { apiClient } from "@/api/apiClient";
import { normalizeDailyStats, type DailyStatsWire } from "@/api/statisticsMappers";
import type {
  AggregatedDealRow,
  AnalyticsBucket,
  AnalyticsTimeSeriesPoint,
  DailyStats,
  Deal,
  PeriodInfo,
  SummaryStats,
} from "@/types/statistics";
import { withBrowserTimezone, withCycleStartTimezone } from "@/utils/timezone";

interface DealsParams {
  start_date?: string;
  end_date?: string;
  instrument_type?: string;
  stock_account_id?: string;
  bot_id?: number;
  demo?: boolean;
  group_by_cycle_start_day?: boolean;
  completed_only?: boolean;
  timezone?: string;
}

interface PeriodParams {
  bot_id?: number;
  stock_account_id?: string;
  completed_only?: boolean;
}

interface SummaryParams {
  start_date?: string;
  end_date?: string;
  bot_id?: number;
  stock_account_id?: string;
  group_by_cycle_start_day?: boolean;
  timezone?: string;
  completed_only?: boolean;
}

interface DailyStatsParams {
  start_date?: string;
  end_date?: string;
  bot_id?: number;
  stock_account_id?: string;
  group_by_cycle_start_day?: boolean;
  timezone?: string;
  completed_only?: boolean;
}

interface TimeSeriesParams {
  start_date: string;
  end_date: string;
  bucket?: AnalyticsBucket;
  bot_id?: number;
  stock_account_id?: string;
  group_by_cycle_start_day?: boolean;
  timezone?: string;
  completed_only?: boolean;
}

class StatisticsService {
  async getDeals(params: DealsParams = {}): Promise<Deal[]> {
    try {
      const response = await apiClient.get<Deal[]>("/v1/statistics/deals", {
        params: withCycleStartTimezone({
          ...params,
          completed_only: params.completed_only ?? false,
        }),
      });
      return response.data;
    } catch (error) {
      console.error("Ошибка при получении сделок:", error);
      throw error;
    }
  }

  async getAggregatedDeals(params: DealsParams = {}): Promise<AggregatedDealRow[]> {
    try {
      const response = await apiClient.get<AggregatedDealRow[]>("/v1/statistics/deals/aggregated", {
        params: withCycleStartTimezone({
          ...params,
          completed_only: params.completed_only ?? false,
        }),
      });
      return response.data;
    } catch (error) {
      console.error("Ошибка при получении агрегированных сделок:", error);
      throw error;
    }
  }

  async getAvailablePeriod(params: PeriodParams = {}): Promise<PeriodInfo> {
    try {
      const response = await apiClient.get<{
        min_exit_time: string | null;
        max_exit_time: string | null;
      }>("/v1/statistics/deals/available-period", {
        params: { ...params, completed_only: params.completed_only ?? false },
      });

      return {
        start_date: response.data.min_exit_time
          ? new Date(response.data.min_exit_time)
          : new Date(),
        end_date: response.data.max_exit_time ? new Date(response.data.max_exit_time) : new Date(),
      };
    } catch (error) {
      console.error("Ошибка при получении доступного периода:", error);
      throw error;
    }
  }

  async getSummary(params: SummaryParams = {}): Promise<SummaryStats> {
    try {
      const response = await apiClient.get<SummaryStats>("/v1/statistics/deals/summary", {
        params: withCycleStartTimezone({
          ...params,
          completed_only: params.completed_only ?? false,
        }),
      });
      return response.data;
    } catch (error) {
      console.error("Ошибка при получении сводной статистики:", error);
      throw error;
    }
  }

  async getDailyStats(params: DailyStatsParams = {}): Promise<DailyStats[]> {
    try {
      const response = await apiClient.get<DailyStatsWire[]>("/v1/statistics/deals/daily", {
        params: withCycleStartTimezone({
          ...params,
          completed_only: params.completed_only ?? false,
        }),
      });
      return normalizeDailyStats(response.data);
    } catch (error) {
      console.error("Ошибка при получении ежедневной статистики:", error);
      throw error;
    }
  }

  async getTimeSeries(params: TimeSeriesParams): Promise<AnalyticsTimeSeriesPoint[]> {
    try {
      const response = await apiClient.get<AnalyticsTimeSeriesPoint[]>(
        "/v1/statistics/deals/time-series",
        {
          params: withBrowserTimezone({
            ...params,
            group_by_cycle_start_day: params.group_by_cycle_start_day ?? true,
            completed_only: params.completed_only ?? false,
          }),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Ошибка при получении временного ряда аналитики:", error);
      throw error;
    }
  }
}

export const statisticsService = new StatisticsService();
